import { useParams } from "react-router-dom";
import { useGetAdminQuizQuery, useUpdateAdminQuizMutation, useGetAdminQuizDifficultiesQuery, useGetAdminQuizSourceMaterialQuery, useGetAdminQuizSourceMaterialDownloadUrlLazyQuery, useActivateAdminQuizMutation, useDeactivateAdminQuizMutation, useRejectAdminQuizMutation, useDeleteAdminQuizMutation } from "../../../lib/apollo/graphql/quiz/quiz-generated";
import PageWrapper from "../../common/components/PageWrapper";
import QuizDetail from "../components/QuizDetail";
import { Quiz } from "./QuizManagementContainer";

export interface QuizDetailData {
  quiz: Quiz;
  question: string;
  hint?: string;
  explanation: string;
  document: string;
  generationJobId?: string;
  choices?: Array<{
    id: string;
    label: string;
    text: string;
    isCorrect: boolean;
  }>;
  shortAnswerSpec?: {
    acceptableAnswers: string[];
    typoTolerance: number;
  };
}

// Helper functions to map API data
const mapQuizStatus = (status: string): "활성" | "비활성" | "신규" | "반려" => {
  switch (status) {
    case "ACTIVE":
      return "활성";
    case "DELETED":
    case "ON_HOLD":
      return "비활성";
    case "REJECTED":
      return "반려";
    case "DRAFTED":
      return "신규";
    default:
      return "신규";
  }
};

const mapQuizType = (type: string): "객관식" | "O/X" | "단답형" => {
  switch (type) {
    case "MCQ":
      return "객관식";
    case "OX":
      return "O/X";
    case "SHORT":
      return "단답형";
    default:
      return "객관식";
  }
};

const mapDifficulty = (difficulty: string): "쉬움" | "적당함" | "어려움" => {
  switch (difficulty) {
    case "EASY":
      return "쉬움";
    case "MEDIUM":
      return "적당함";
    case "HARD":
      return "어려움";
    default:
      return "적당함";
  }
};

export default function QuizDetailContainer() {
  const { quizId } = useParams<{ quizId: string }>();
  const { data, loading, refetch } = useGetAdminQuizQuery({
    variables: { id: quizId || "" },
    skip: !quizId,
  });
  const { data: difficultiesData } = useGetAdminQuizDifficultiesQuery({
    variables: { includeInactive: false },
    fetchPolicy: "network-only",
  });

  // 기반자료 정보 조회
  const sourceMaterialId = data?.quiz?.sourceMaterialId;
  const { data: sourceMaterialData } = useGetAdminQuizSourceMaterialQuery({
    variables: { id: sourceMaterialId || "" },
    skip: !sourceMaterialId,
  });

  const [getDownloadUrl] = useGetAdminQuizSourceMaterialDownloadUrlLazyQuery();
  const [updateQuiz] = useUpdateAdminQuizMutation();
  const [activateQuiz] = useActivateAdminQuizMutation();
  const [deactivateQuiz] = useDeactivateAdminQuizMutation();
  const [rejectQuiz] = useRejectAdminQuizMutation();
  const [deleteQuiz] = useDeleteAdminQuizMutation();

  // Transform API data to UI format
  // 활성화된 난이도 목록을 order 순으로 정렬
  const difficulties = difficultiesData?.difficulties
    ? [...difficultiesData.difficulties].sort((a, b) => a.order - b.order)
    : [];

  const quizDetailData: QuizDetailData | null = data?.quiz
    ? {
        quiz: {
          quizId: data.quiz.id,
          quizNumber: parseInt(data.quiz.id) || 0,
          status: mapQuizStatus(data.quiz.status),
          title: data.quiz.stem,
          type: mapQuizType(data.quiz.type),
          difficulty: mapDifficulty(data.quiz.difficultyLevel?.code || "MEDIUM"),
          points: data.quiz.awardedPoints,
          createdAt: data.quiz.createdAt,
          updatedAt: data.quiz.updatedAt,
          lastModifier: data.quiz.updatedBy || "-",
        },
        question: data.quiz.stem,
        hint: data.quiz.hint || undefined,
        explanation: data.quiz.explanation,
        document: sourceMaterialData?.sourceMaterial?.title || "기반자료 정보 없음",
        generationJobId: data.quiz.generationJobId || undefined,
        choices: data.quiz.choices?.map((choice: any) => ({
          id: choice.id,
          label: choice.label,
          text: choice.text,
          isCorrect: choice.isCorrect,
        })),
        shortAnswerSpec: data.quiz.shortAnswerSpec
          ? {
              acceptableAnswers: data.quiz.shortAnswerSpec.acceptableAnswers,
              typoTolerance: data.quiz.shortAnswerSpec.typoTolerance,
            }
          : undefined,
      }
    : null;

  const handleSaveQuiz = async (updatedQuiz: any, difficultyId?: string, awardedPoints?: number) => {
    if (!quizId) return;

    try {
      // Prepare input with all fields
      const input: any = {
        stem: updatedQuiz.question || updatedQuiz.title,
        difficultyId: difficultyId || data?.quiz?.difficultyLevel?.id,
        awardedPoints: awardedPoints !== undefined ? awardedPoints : updatedQuiz.points,
        explanation: updatedQuiz.explanation || "",
        hint: updatedQuiz.hint?.trim() || undefined,
      };

      // Add type-specific fields
      if (updatedQuiz.type === "객관식") {
        const labels = ["A", "B", "C", "D", "E"];
        input.choices = updatedQuiz.options.map((option: any, index: number) => ({
          label: labels[index],
          text: option.content,
          isCorrect: option.isCorrect,
        }));
      } else if (updatedQuiz.type === "O/X") {
        // For O/X, create two choices based on the answer
        const oxAnswer = updatedQuiz.oxAnswer || "O";
        input.choices = [
          {
            label: "A",
            text: "O",
            isCorrect: oxAnswer === "O",
          },
          {
            label: "B",
            text: "X",
            isCorrect: oxAnswer === "X",
          },
        ];
      } else if (updatedQuiz.type === "단답형") {
        // For short answer, use typoTolerance from original data if not provided
        const typoTolerance = updatedQuiz.typoTolerance !== undefined
          ? updatedQuiz.typoTolerance
          : (data?.quiz?.shortAnswerSpec?.typoTolerance || 0);

        input.shortAnswerSpec = {
          acceptableAnswers: updatedQuiz.shortAnswers?.map((a: any) => a.content) || [],
          typoTolerance,
        };
      }

      await updateQuiz({
        variables: {
          id: quizId,
          input,
        },
      });

      alert("퀴즈가 수정되었습니다.");
      refetch();
    } catch (error) {
      console.error("Quiz update error:", error);
      alert("퀴즈 수정에 실패했습니다.");
    }
  };

  const handleDownloadDocument = async () => {
    if (!sourceMaterialId) {
      alert("기반자료 정보가 없습니다.");
      return;
    }

    try {
      const { data } = await getDownloadUrl({ variables: { id: sourceMaterialId } });
      const url = data?.getSourceMaterialDownloadUrl;

      if (!url) {
        alert("파일에 문제가 있습니다. 데이터 관리자에게 문의하세요.");
        return;
      }

      // Open URL in new tab for download
      window.open(url, "_blank");
    } catch (error) {
      console.error("Download URL fetch error:", error);
      alert("파일에 문제가 있습니다. 데이터 관리자에게 문의하세요.");
    }
  };

  const handleActivate = async () => {
    if (!quizId) return;

    try {
      await activateQuiz({ variables: { id: quizId } });
      refetch();
    } catch (error) {
      console.error("Activate quiz error:", error);
      alert("퀴즈 활성화에 실패했습니다.");
      throw error;
    }
  };

  const handleDeactivate = async () => {
    if (!quizId) return;

    try {
      await deactivateQuiz({ variables: { id: quizId } });
      refetch();
    } catch (error) {
      console.error("Deactivate quiz error:", error);
      alert("퀴즈 비활성화에 실패했습니다.");
      throw error;
    }
  };

  const handleReject = async () => {
    if (!quizId) return;

    try {
      await rejectQuiz({ variables: { id: quizId } });
      refetch();
    } catch (error) {
      console.error("Reject quiz error:", error);
      alert("퀴즈 거부에 실패했습니다.");
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!quizId) return;

    if (!window.confirm("퀴즈를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.")) return;

    try {
      await deleteQuiz({ variables: { id: quizId } });
      alert("퀴즈가 삭제되었습니다.");
      window.history.back();
    } catch (error) {
      console.error("Delete quiz error:", error);
      alert("퀴즈 삭제에 실패했습니다.");
    }
  };

  if (loading) {
    return (
      <PageWrapper className="overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">로딩 중...</div>
        </div>
      </PageWrapper>
    );
  }

  if (!quizDetailData) {
    return (
      <PageWrapper className="overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">퀴즈를 찾을 수 없습니다.</div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper className="overflow-y-auto">
      <QuizDetail
        quizData={quizDetailData}
        onSaveQuiz={handleSaveQuiz}
        onDownloadDocument={handleDownloadDocument}
        difficulties={difficulties}
        currentDifficultyId={data?.quiz?.difficultyLevel?.id}
        onActivate={handleActivate}
        onDeactivate={handleDeactivate}
        onReject={handleReject}
        onDelete={handleDelete}
      />
    </PageWrapper>
  );
}